import React, {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {BACKEND} from '../../consts/backend';
import LogoLight2x from '../../images/ssb_white.158c7b19.png';
import Aviso from '../../plasticos/components/Aviso';
import {Icon} from '../../components/Component';
import CryptoJS from 'crypto-js';
import Head from '../../layout/head/Head';
import { QRCodeCanvas } from 'qrcode.react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [avisos, setAvisos] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [Loading, setLoading] = useState(false);

  // 2FA states
  const [twoFaStep, setTwoFaStep]           = useState(null); // null | 'setup' | 'required'
  const [tempToken, setTempToken]           = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [totpCode, setTotpCode]             = useState('');
  const [twoFaLoading, setTwoFaLoading]     = useState(false);

  const history = useHistory();

  // Asegurar que el título se establezca correctamente al montar el componente
  useEffect(() => {
    document.title = "Login - Metrics";
  }, []);

  // Mostrar avisos
  const mostrarAviso = (titulo, estado, paraDetail) => {
    const nuevoAviso = {titulo, estado, paraDetail};
    setAvisos([...avisos, nuevoAviso]);
  };

  // Eliminar avisos después de 3 segundos
  useEffect(() => {
    if (avisos.length > 0) {
      const timeout = setTimeout(() => {
        setAvisos(avisos.slice(1));
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [avisos]);

  const CLAVE_AES = process.env.REACT_APP_CLAVE_AES; // DEBE SER LA MISMA QUE EN EL BACKEND

  const cifrarPassword = (password) => {
    const iv = CryptoJS.lib.WordArray.random(16);  // IV de 16 bytes
    const claveHash = CryptoJS.SHA256(CLAVE_AES);  // 32 bytes (SHA-256)
    const opciones = { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 };
  
    const textoCifrado = CryptoJS.AES.encrypt(password, claveHash, opciones);  // Cifrado con AES-256
    
    // Concatenamos IV y ciphertext
    const textoConcatenado = iv.concat(textoCifrado.ciphertext);
    // Convertimos todo a Base64
    return textoConcatenado.toString(CryptoJS.enc.Base64);  // Codificamos todo a Base64
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {

      const encryptedPassword = cifrarPassword(password)

      const response = await fetch(`${BACKEND}/login`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({email, password: encryptedPassword}),
      }); 
      if (!response.ok) {
        const data = await response.json();
        //setError(data.message || 'Credenciales incorrectas');
        mostrarAviso(
          data.message || 'Credenciales incorrectas',
          false,
          false
        );
        return;
      }
      const data = await response.json();

      // 2FA challenge
      if (data.status === '2fa_setup' || data.status === '2fa_required') {
        setTempToken(data.temp_token);
        if (data.status === '2fa_setup') {
          setProvisioningUri(data.provisioning_uri || '');
          setTwoFaStep('setup');
        } else {
          setTwoFaStep('required');
        }
        return;
      }

      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('lastActivity', Date.now().toString()); // Establecer timestamp inicial

      // Si el rol es Remitos, asignar ssb_internacional automáticamente y saltear la selección
      if (data.user?.rol_metric_name?.toLowerCase() === 'remitos') {
        const dowCompany = {
          id: 'dow',
          name: 'DOW',
          database: 'ssb_internacional',
          description: 'Dow Chemical Company',
          color: '#e74c3c',
          logo: '🏭'
        };
        localStorage.setItem('selectedCompany', JSON.stringify(dowCompany));
        localStorage.setItem('currentDatabase', 'ssb_internacional');
        try {
          await fetch(`${BACKEND}/db/set/ssb_internacional`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
        } catch (_) { /* continuar aunque falle el endpoint */ }
        history.push('/');
        return;
      }

      // Redirigir a selección de empresa después del login exitoso
      history.push('/company-selection');
    } catch (err) {
      mostrarAviso('Error de conexión con el servidor', false, false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit2FA = async (e) => {
    e.preventDefault();
    setTwoFaLoading(true);
    try {
      const response = await fetch(`${BACKEND}/login/2fa`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ temp_token: tempToken, totp_code: totpCode }),
      });
      const data = await response.json();
      if (!response.ok) {
        mostrarAviso(data.message || 'Código incorrecto', false, false);
        setTotpCode('');
        return;
      }
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('lastActivity', Date.now().toString());
      if (data.user?.rol_metric_name?.toLowerCase() === 'remitos') {
        const dowCompany = {
          id: 'dow', name: 'DOW', database: 'ssb_internacional',
          description: 'Dow Chemical Company', color: '#e74c3c', logo: '🏭'
        };
        localStorage.setItem('selectedCompany', JSON.stringify(dowCompany));
        localStorage.setItem('currentDatabase', 'ssb_internacional');
        try {
          await fetch(`${BACKEND}/db/set/ssb_internacional`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
        } catch (_) { /* continuar */ }
        history.push('/');
        return;
      }
      history.push('/company-selection');
    } catch (err) {
      mostrarAviso('Error de conexión con el servidor', false, false);
    } finally {
      setTwoFaLoading(false);
    }
  };

  return (
    <>
      <Head title="Login - Metrics" />
      {avisos.map((aviso, index) => (
        <Aviso
          key={index}
          titulo={aviso.titulo}
          estado={aviso.estado}
          paraDetail={aviso.paraDetail}
        />
      ))}
      <div style={styles.container}>
        <div style={styles.card}>
          <img src={LogoLight2x} alt="Logo" style={styles.logo} />

          {/* ── 2FA panel ─────────────────────────────────── */}
          {twoFaStep ? (
            <>
              <p style={styles.subtitle}>
                {twoFaStep === 'setup'
                  ? 'Configurá tu autenticador — escaneá el QR con Google Authenticator'
                  : 'Ingresá el código de 6 dígitos de tu autenticador'}
              </p>
              {twoFaStep === 'setup' && provisioningUri && (
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <QRCodeCanvas value={provisioningUri} size={220} level="M" includeMargin />
                  <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                    Escaneá este código con Google Authenticator y luego ingresá el código de 6 dígitos.
                  </p>
                  <p style={{ fontSize: '10px', color: '#aaa', marginTop: '4px', wordBreak: 'break-all', maxWidth: '300px', margin: '4px auto 0' }}>
                    {provisioningUri}
                  </p>
                </div>
              )}
              <form onSubmit={handleSubmit2FA} style={styles.form}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Código 2FA</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    style={styles.input}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    autoFocus
                    required
                  />
                </div>
                <button type="submit" style={styles.loginButton} disabled={twoFaLoading}>
                  {twoFaLoading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <p>VERIFICAR</p>}
                </button>
                <button
                  type="button"
                  style={{ ...styles.loginButton, marginTop: '8px', background: '#6c757d' }}
                  onClick={() => { setTwoFaStep(null); setTempToken(''); setTotpCode(''); }}
                >
                  <p>Volver</p>
                </button>
              </form>
            </>
          ) : (
          <>
          <p style={styles.subtitle}>
            Please enter your login and password!
          </p>

          {/*error && <div style={styles.error}>{error}</div>*/}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label htmlFor="email" style={styles.label}>
                Email
              </label>
              <input
                type="email"
                id="email"
                style={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div style={styles.inputGroup}>
              <label htmlFor="password" style={styles.label}>
                Password
              </label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  style={styles.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  style={styles.toggleButton}
                >
                  <Icon name={showPassword ? 'eye-off' : 'eye'} />
                </button>
              </div>
            </div>

            {/*<a href="#!" style={styles.forgotPassword}>Forgot password?</a>*/}

            <button type="submit" style={styles.loginButton}>
              {Loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <p>LOGIN</p>
              )}
            </button>
          </form>

          {/*<p style={styles.signupText}>
          Don't have an account? <a href="#!" style={styles.signupLink}>Sign Up</a>
        </p>*/}
          </>
          )} {/* end twoFaStep conditional */}
        </div>
      </div>
    </>
  );
};

// Estilos en línea (puedes moverlos a un archivo CSS aparte si prefieres)
const styles = {
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  passwordInput: {
    flex: 1,
    padding: '14px',
    borderRadius: '8px',
    border: '2px solid rgba(196, 206, 254, 0.3)',
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.3s ease',
    paddingRight: '45px', // espacio para el icono
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  toggleButton: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#666',
    borderRadius: '4px',
    transition: 'color 0.3s ease',
  },

  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    backgroundColor: '#18263f',
    borderRadius: '8px',
    padding: '40px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#333',
  },
  subtitle: {
    fontSize: '14px',
    color: '#c4cefe',//rgb(119 161 214)
    marginBottom: '30px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    textAlign: 'left',
  },
  label: {
    fontSize: '14px',
    color: 'white',//#c4cefe
    fontWeight: '500',
  },
  input: {
    padding: '14px',
    borderRadius: '8px',
    border: '2px solid rgba(196, 206, 254, 0.3)',
    fontSize: '16px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  inputFocus: {
    border: '2px solid #c4cefe',
    boxShadow: '0 4px 12px rgba(196, 206, 254, 0.3)',
  },
  forgotPassword: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'right',
    textDecoration: 'none',
    marginTop: '-10px',
  },
  loginButton: {
    padding: '14px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: 'rgb(214, 101, 19)',
    borderColor: 'rgb(214, 101, 19)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(214, 101, 19, 0.3)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  signupText: {
    fontSize: '14px',
    color: '#666',
    marginTop: '20px',
  },
  signupLink: {
    color: '#000',
    fontWeight: 'bold',
    textDecoration: 'none',
  },
  error: {
    color: '#ff4444',
    fontSize: '14px',
    marginBottom: '20px',
    padding: '10px',
    backgroundColor: '#ffebee',
    borderRadius: '4px',
  },
  logo: {
    width: '300px', // o el tamaño que prefieras
  },
};

export default Login;
